import {Formik, FormikConfig} from 'formik'
import {FC, useCallback, useMemo, useState} from 'react'
import {useMutation, useQuery} from 'react-query'
import {Outlet, useLocation, useNavigate, useParams} from 'react-router-dom'

import {BookingQueries} from '../../../api/booking'
import {
  commonServiceBookingDataAdapter,
  commonServiceBookingPayByTokenAdapter,
  PayByTokenServiceBookingDraft,
  removeFormFromBookingData} from '../../../api/booking/helpers'
import {ServiceItem} from '../../../api/booking/types'
import {getEntityDataByIdFromAppearanceSettings} from '../../../api/helpers/data'
import {handlePromoPayError} from '../../../components/promo-code-field/handle-promo-pay-error'
import {handleAddonInvalidError} from '../../../helpers/addon-error-message'
import {isContactBlockedError, showBlockedMessage} from '../../../helpers/blocked-member'
import {usePassNotValidHandler} from '../../../helpers/use-pass-not-valid-handler'
import {auth} from '../../../utils/firebase'
import {useLuxon} from '../../../utils/i18n'
import {useModalActions} from '../../../utils/modals'
import {defaultUserValues} from '../../auth/forms/integrated-sign-up-form'
import {showChargeRetryHintIfDegraded} from '../../payments/helpers/charge-retry-hint'
import {useHandleDraftEntityStatusChange} from '../../payments/hooks/handle-draft-entity-status-change.hook'
import {usePaymentProvider} from '../../payments/hooks/payment-provider-form.provider'
import {useBookingLoader} from '../hooks'
import {BookingService} from '../index'
import {AppearanceEntityGate} from './appearance-entity-gate'
import {RedirectionComponent} from './components/redirection-component'
import {CreateServiceBookingFormType} from './create-service-booking-form'
import {useOpenBookingValidationModal} from './hooks/open-booking-validation-modal.hook'
import {resolveMinParticipants} from './min-participants'

type Params = {
  venueId: string
  serviceId: string
}

const CreateSpaceBookingFormInner: FC = () => {
  const luxon = useLuxon()
  const {venueId, serviceId} = useParams<Params>()
  const [entityId, setEntityId] = useState<string | null>(null)
  const navigate = useNavigate()
  const {closeAllModals} = useModalActions()
  const {isLoading, setIsLoading} = useBookingLoader()
  const {openPaymentForm} = usePaymentProvider()
  const {mutate: createDraftBooking} = useMutation(BookingService.mutations.createServiceBookingDraftMutation({}))
  const {data: priceDataObjects} = useQuery(BookingQueries.calculateBookingPriceQuery({enabled: false}))

  // const initialUserId = useMemo(() => auth.currentUser?.uid, [])

  if (!venueId || !serviceId) {
    return null
  }

  const location = useLocation()
  const state = location.state as Record<string, unknown> | undefined

  const service = getEntityDataByIdFromAppearanceSettings(venueId, serviceId) as ServiceItem
  const {mutate: createServiceBooking} = useMutation(BookingService.mutations.createServiceBookingMutation({}))
  const {mutate: createServiceBookingPayByToken} = useMutation(
    BookingService.mutations.createServiceBookingPayByTokenMutation({}),
  )
  const {setStartRefetch} = useHandleDraftEntityStatusChange({
    entity: 'booking',
    entityId,
    isLoading,
    setIsLoading,
  })
  const {openBookingValidationModal} = useOpenBookingValidationModal()
  const handlePassNotValid = usePassNotValidHandler()

  const initialValues = useMemo<CreateServiceBookingFormType>(
    () => ({
      ...defaultUserValues,
      service,
      area: null,
      teacher: null,
      participants: [],
      entity: 'service',
      specialRequest: '',
      minParticipants: resolveMinParticipants(service),
      time: [null, null],
      bookingType: 'space',
      isJustTheSpace: true,
      splitPayment: false,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      serviceId: serviceId!,
      paymentMethodId: null,
      hasSpecialRequests: false,
      maxParticipants: Infinity,
      venueId: venueId ? venueId : '',
      date: luxon.now().toFormat('dd/MM/yyyy'),
    }),
    [venueId, service, serviceId, luxon, state?.service],
  )
  if (state?.service) {
    // @ts-ignore
    initialValues.service = state.service
  }


  const handleSubmit = useCallback<FormikConfig<CreateServiceBookingFormType>['onSubmit']>(
    (values, formikHelpers) => {
      const {setFieldValue} = formikHelpers
      if (!priceDataObjects) {
        return
      }
      const specialRequestElement = typeof document !== 'undefined' ? (document.getElementById('specialRequest') as HTMLInputElement | HTMLTextAreaElement | null) : null
      const specialRequestFromDom = specialRequestElement?.value ?? ''
      const valuesWithSpecialRequest: CreateServiceBookingFormType = values.hasSpecialRequests
        ? {...values, specialRequest: specialRequestFromDom}
        : {...values, specialRequest: ''}
      // if (initialUserId !== auth.currentUser?.uid && service.accessForAnyone !== false) {
      //   return openBookingValidationModal({type: 'service'})
      // }
      const isPayNow = values.paymentType !== 'payOnLocation'
      const createBookingFunction = isPayNow ? createServiceBookingPayByToken : createServiceBooking
      const dataForAdapter = {
        bookingState: {
          ...valuesWithSpecialRequest,
          userId: auth.currentUser?.uid,
        },
        priceDataObjects,
      }
      let adaptedData = null
      let dataForDraft: PayByTokenServiceBookingDraft | null = null
      if (values.paymentType !== 'payOnLocation') {
        adaptedData = commonServiceBookingPayByTokenAdapter(dataForAdapter)
        dataForDraft = adaptedData
      } else {
        adaptedData = commonServiceBookingDataAdapter(dataForAdapter)
      }
      if (values.paymentType === 'oneTimePayment') {
        if (dataForDraft === null) {
          return
        }
        const dataForDraftToUse = dataForDraft as PayByTokenServiceBookingDraft
        const submitDraft = (data: any, isRetry = false) => {
          createDraftBooking(
            data,
            {
              onSuccess: () => {
                setStartRefetch(true)
                setEntityId(dataForDraftToUse.bookingId || null)
                openPaymentForm && openPaymentForm({
                  entity: 'booking',
                  bookingId: dataForDraftToUse.bookingId,
                  startProcessCallback: () => setIsLoading(true),
                  totalPrice: dataForDraftToUse.data.splitPayment ? dataForDraftToUse.data.paidAmount : dataForDraftToUse.data.totalPrice,
                })
              },
              onError: (error: any) => {
                if (error?.response?.status === 400 && (error?.response?.data?.code === 'FORM_INACTIVE' || error?.response?.data?.message === 'Form is inactive or archived') && !isRetry) {
                  submitDraft(removeFormFromBookingData(data), true)
                  return
                }
                if (handleAddonInvalidError(error)) {
                  return
                }
                if (handlePromoPayError(error, setFieldValue, 'calculateBookingPriceQuery')) {
                  return
                }
                if (handlePassNotValid(error, {participants: values.participants, setFieldValue})) {
                  return
                }
                if (isContactBlockedError(error)) {
                  return showBlockedMessage()
                }
                if (error?.response?.status === 400 && error?.response?.data?.type === 'TIME_SLOT_NOT_AVAILABLE') {
                  setFieldValue('time', [null, null])
                  openBookingValidationModal({type: 'service'})
                }
              },
            }
          )
        }
        submitDraft(adaptedData)
      } else {
        setEntityId(null)
        setIsLoading(true)
        const submitBooking = (data: any, isRetry = false) => {
          createBookingFunction(
            data,
            {
              onSuccess: (booking) => {
                closeAllModals()
                setIsLoading(false)
                const uid = booking.uid ?? (booking as { bookingId?: string }).bookingId
                if (venueId && uid) {
                  navigate(`/${venueId}/bookings/${uid}`, {replace: true})
                }
              },
              onError: (error: any) => {
                console.log(error)
                if (error?.response?.status === 400 && (error?.response?.data?.code === 'FORM_INACTIVE' || error?.response?.data?.message === 'Form is inactive or archived') && !isRetry) {
                  submitBooking(removeFormFromBookingData(data), true)
                  return
                }
                setIsLoading(false)
                if (handleAddonInvalidError(error)) {
                  return
                }
                if (handlePromoPayError(error, setFieldValue, 'calculateBookingPriceQuery')) {
                  return
                }
                if (handlePassNotValid(error, {participants: values.participants, setFieldValue})) {
                  return
                }
                if (isContactBlockedError(error)) {
                  return showBlockedMessage()
                }
                if (error.response?.status === 409) {
                  setFieldValue('time', [null, null])
                  return openBookingValidationModal({type: 'service'})
                }
                if (isPayNow) {
                  showChargeRetryHintIfDegraded(venueId)
                }
              },
            },
          )
        }
        submitBooking(adaptedData)
      }
    }, [
    venueId,
    navigate,
    setIsLoading,
    closeAllModals,
    openPaymentForm,
    priceDataObjects,
    createServiceBooking,
    openBookingValidationModal,
    createServiceBookingPayByToken,
    handlePassNotValid,
  ],
  )

  return (
    <Formik
      validateOnChange={false}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <RedirectionComponent>
        <Outlet />
      </RedirectionComponent>
    </Formik>
  )
}

export const CreateSpaceBookingForm: FC = () => (
  <AppearanceEntityGate entityParams={['serviceId']}>
    <CreateSpaceBookingFormInner />
  </AppearanceEntityGate>
)
